import { Link } from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import { cn } from '#app/utils/misc.tsx'

export interface HeroModernProps {
	title: string
	subtitle?: string
	supportingText?: string
	buttons?: {
		text: string
		href: string
		variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link'
	}[]
	image?: {
		url: string
		alt?: string
	}
	align?: 'left' | 'center'
}

function renderHeroTitle(title: string) {
	const marker = ', en un sitio'
	if (!title.includes(marker)) return title

	const [prefix, suffixRest] = title.split(marker)
	const suffix = `${marker}${suffixRest ?? ''}`
	const secondaryMarker = ' al que siempre puedes volver'

	if (!suffix.includes(secondaryMarker)) {
		return (
			<>
				{prefix}
				<br className="hidden sm:block" />{' '}
				<span className="text-muted-foreground">{suffix}</span>
			</>
		)
	}

	const [secondaryPrefix, secondarySuffixRest] = suffix.split(secondaryMarker)
	const secondarySuffix = `${secondaryMarker}${secondarySuffixRest ?? ''}`

	return (
		<>
			{prefix}
			<br className="hidden sm:block" />{' '}
			<span className="text-muted-foreground">
				{secondaryPrefix}
				<br className="hidden sm:block" />
				{secondarySuffix}
			</span>
		</>
	)
}

export function HeroModern({
	title,
	subtitle,
	supportingText,
	buttons,
	align = 'center',
}: HeroModernProps) {
	return (
		<section className="relative isolate overflow-hidden px-6 pt-20 pb-14 sm:px-8 sm:pt-24 sm:pb-16 lg:px-12">
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 -z-10"
				style={{
					background:
						'linear-gradient(180deg, var(--landing-top-tint) 0%, var(--background) 100%)',
				}}
			/>
			<div
				aria-hidden
				className="pointer-events-none absolute top-0 left-1/2 -z-10 h-[420px] w-[960px] -translate-x-1/2 rounded-full opacity-40 blur-3xl"
				style={{
					background:
						'radial-gradient(ellipse at center, var(--landing-glow), transparent 70%)',
				}}
			/>
			<div
				className={cn(
					'mx-auto flex max-w-4xl flex-col gap-8',
					align === 'center'
						? 'items-center text-center'
						: 'items-start text-left',
				)}
			>
				<div
					className={cn(
						'flex flex-col gap-6',
						align === 'center' ? 'items-center' : 'items-start',
					)}
				>
					<h1 className="text-display">{renderHeroTitle(title)}</h1>
					{subtitle && (
						<p className="text-muted-foreground max-w-2xl text-base leading-relaxed text-pretty sm:text-lg">
							{subtitle}
						</p>
					)}
				</div>

				{buttons && buttons.length > 0 && (
					<div
						className={cn(
							'flex flex-col gap-3 sm:flex-row sm:gap-4',
							align === 'center' ? 'items-center' : 'items-start',
						)}
					>
						{buttons.map((button, index) => (
							<Button
								key={index}
								variant={button.variant || 'default'}
								asChild
								size="lg"
								className="px-8"
							>
								<Link to={button.href}>{button.text}</Link>
							</Button>
						))}
					</div>
				)}

				{supportingText ? (
					<p className="text-caption text-muted-foreground">{supportingText}</p>
				) : null}
			</div>
		</section>
	)
}
